import React from 'react';
import { Plane, Clock, ArrowRight, ChevronDown, ChevronUp, RefreshCcw, Luggage, Ban } from 'lucide-react';
import { getFareTypeInfo } from '../utils/fareTypeMapping';
import DesktopFlightDetails from './DesktopFlightDetails';
import FareRulesCompact from './FareRulesCompact';

const ComboFlightCard = ({
  flight,
  airlineLogos,
  cheapestPrice,
  isExpanded,
  onToggleExpand,
  onBook,
  selectedFare,
  onFareSelect,
  activeTab,
  setActiveTab,
  fareRulesData,
  loadingFareRulesData,
  onFareRulesOpen,
}) => {
  // Helper to calculate total duration from segments
  const calculateDuration = (segments) => {
    if (!segments || segments.length === 0) return 'N/A';
    const totalMinutes = segments.reduce((sum, seg) => sum + (seg.duration || 0), 0);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  // Helper to get segment time
  const getSegmentTime = (segments, type) => {
    if (!segments || segments.length === 0) return 'N/A';
    const segment = type === 'departure' ? segments[0] : segments[segments.length - 1];
    const time = type === 'departure' ? segment.dt : segment.at;
    return time ? time.split('T')[1]?.substring(0, 5) : 'N/A';
  };

  // Helper to get airport info
  const getAirport = (segments, type) => {
    if (!segments || segments.length === 0) return { code: 'XXX', city: 'N/A' };
    const segment = type === 'departure' ? segments[0] : segments[segments.length - 1];
    const airport = type === 'departure' ? segment.da : segment.aa;
    return {
      code: airport?.code || 'XXX',
      city: airport?.city || 'N/A'
    };
  };

  const onwardDepartureTime = getSegmentTime(flight.onwardSegments, 'departure');
  const onwardArrivalTime = getSegmentTime(flight.onwardSegments, 'arrival');
  const returnDepartureTime = getSegmentTime(flight.returnSegments, 'departure');
  const returnArrivalTime = getSegmentTime(flight.returnSegments, 'arrival');

  const onwardFrom = getAirport(flight.onwardSegments, 'departure');
  const onwardTo = getAirport(flight.onwardSegments, 'arrival');
  const returnFrom = getAirport(flight.returnSegments, 'departure');
  const returnTo = getAirport(flight.returnSegments, 'arrival');

  const onwardDuration = calculateDuration(flight.onwardSegments);
  const returnDuration = calculateDuration(flight.returnSegments);

  const onwardStops = flight.onwardSegments?.length > 1 
    ? `${flight.onwardSegments.length - 1} Stop${flight.onwardSegments.length > 2 ? 's' : ''}` 
    : 'Non-stop';
  const returnStops = flight.returnSegments?.length > 1 
    ? `${flight.returnSegments.length - 1} Stop${flight.returnSegments.length > 2 ? 's' : ''}` 
    : 'Non-stop';

  // Get baggage from first segment (usually consistent across all segments)
  const baggage = flight.onwardSegments?.[0]?.baggage || flight.baggage || '15 Kg';
  const cabinBaggage = flight.onwardSegments?.[0]?.cabinBaggage || flight.cabinBaggage || '7 Kg';

  // Get refundable status from API data
  const isRefundable = flight.isRefundable !== undefined ? flight.isRefundable : true;
  const refundableText = isRefundable ? 'Refundable' : 'Non-Refundable';

  // Get airline info
  const airlineCode = flight.airlineCode || flight.onwardSegments?.[0]?.fD?.aI?.code || '';
  const airlineName = flight.airline || flight.onwardSegments?.[0]?.fD?.aI?.name || 'Airline';

  const isSoldOut = flight.isSoldOut || flight.seatsLeft === 0;

  return (
    <div className={`bg-white rounded-xl shadow-sm transition-all duration-200 overflow-hidden border ${
      isSoldOut ? 'opacity-60 border-gray-200/80' : 'hover:shadow-md border-gray-200/80'
    }`}>
      {/* Sold Out banner */}
      {isSoldOut && (
        <div data-testid="combo-flight-card-sold-out-badge" className="bg-gray-700 text-white text-[10px] font-bold px-4 py-1 flex items-center gap-1.5">
          SOLD OUT
        </div>
      )}
      {/* Compact Main Card Content */}
      <div className="p-3 md:p-4">
        {/* Two-column layout: Flights | Price + Book */}
        <div className="flex flex-col md:flex-row items-start gap-4">
          {/* Left: Flight Details - More Compact */}
          <div className="flex-1 w-full grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* ONWARD */}
            <div className="md:border-r border-gray-200 md:pr-4 pb-4 md:pb-0 border-b md:border-b-0">
              {/* Compact Header */}
              <div className="flex items-center gap-2 mb-2 md:mb-3">
                <div className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center flex-shrink-0">
                  {airlineLogos[airlineCode]?.square ? (
                    <img src={airlineLogos[airlineCode].square} alt={airlineName} className="w-full h-full object-contain" />
                  ) : (
                    <Plane className="w-3.5 h-3.5 text-[#659A9B]" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-[#1B375B] leading-tight truncate">Onward Journey</p>
                  <p className="text-[9px] text-gray-500 truncate">{airlineName}</p>
                </div>
              </div>

              {/* Compact Flight Times */}
              <div className="flex items-center justify-between mb-2">
                {/* Departure */}
                <div className="text-left">
                  <p className="text-xl md:text-2xl font-black text-[#1B375B] leading-none">{onwardDepartureTime}</p>
                  <p className="text-xs font-bold text-gray-700 mt-0.5">{onwardFrom.code}</p>
                  <p className="text-[9px] text-gray-500 truncate max-w-[60px] md:max-w-none">{onwardFrom.city}</p>
                </div>

                {/* Duration - Compact */}
                <div className="flex flex-col items-center px-1 min-w-0">
                  <p className="text-[10px] text-gray-500 mb-0.5 whitespace-nowrap">{onwardDuration}</p>
                  <div className="w-10 md:w-12 h-px bg-gray-300 relative">
                    <Plane className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 text-gray-400" />
                  </div>
                  <p className="text-[9px] text-gray-500 mt-0.5">{onwardStops}</p>
                </div>

                {/* Arrival */}
                <div className="text-right">
                  <p className="text-xl md:text-2xl font-black text-[#1B375B] leading-none">{onwardArrivalTime}</p>
                  <p className="text-xs font-bold text-gray-700 mt-0.5">{onwardTo.code}</p>
                  <p className="text-[9px] text-gray-500 truncate max-w-[60px] md:max-w-none">{onwardTo.city}</p>
                </div>
              </div>
            </div>

            {/* RETURN */}
            <div>
              {/* Compact Header */}
              <div className="flex items-center gap-2 mb-2 md:mb-3">
                <div className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center flex-shrink-0">
                  {airlineLogos[airlineCode]?.square ? (
                    <img src={airlineLogos[airlineCode].square} alt={airlineName} className="w-full h-full object-contain" />
                  ) : (
                    <Plane className="w-3.5 h-3.5 text-[#659A9B]" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-[#1B375B] leading-tight truncate">Return Journey</p>
                  <p className="text-[9px] text-gray-500 truncate">{airlineName}</p>
                </div>
              </div>

              {/* Compact Flight Times */}
              <div className="flex items-center justify-between mb-2">
                {/* Departure */}
                <div className="text-left">
                  <p className="text-2xl font-black text-[#1B375B] leading-none">{returnDepartureTime}</p>
                  <p className="text-xs font-bold text-gray-700 mt-0.5">{returnFrom.code}</p>
                  <p className="text-[9px] text-gray-500 truncate">{returnFrom.city}</p>
                </div>

                {/* Duration - Compact */}
                <div className="flex flex-col items-center px-1 min-w-0">
                  <p className="text-[10px] text-gray-500 mb-0.5 whitespace-nowrap">{returnDuration}</p>
                  <div className="w-12 h-px bg-gray-300 relative">
                    <Plane className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 text-gray-400" />
                  </div>
                  <p className="text-[9px] text-gray-500 mt-0.5">{returnStops}</p>
                </div>

                {/* Arrival */}
                <div className="text-right">
                  <p className="text-2xl font-black text-[#1B375B] leading-none">{returnArrivalTime}</p>
                  <p className="text-xs font-bold text-gray-700 mt-0.5">{returnTo.code}</p>
                  <p className="text-[9px] text-gray-500 truncate">{returnTo.city}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Price + Book Button - Compact */}
          <div className="flex flex-row md:flex-col items-center md:items-end justify-between md:justify-start gap-3 md:gap-2 w-full md:w-auto md:min-w-[160px] pt-3 md:pt-0 border-t md:border-t-0 border-gray-200">
            {/* Price - Compact */}
            <div className="text-left md:text-right">
              {flight.originalPrice && flight.originalPrice > (selectedFare?.price || flight.price) && (
                <p className="text-xs text-gray-400 line-through">₹{flight.originalPrice.toLocaleString()}</p>
              )}
              <p className="text-xl md:text-2xl font-black text-[#1B375B] leading-none">
                ₹{(selectedFare?.price || flight.price).toLocaleString()}
              </p>
              <p className="text-[9px] text-gray-400 mt-0.5">per adult</p>
            </div>

            {/* Book Button - ACF Theme Teal */}
            <button
              onClick={() => { if (!isSoldOut) onBook(flight); }}
              disabled={isSoldOut}
              data-testid="combo-flight-card-book-button"
              className={`flex-shrink-0 md:w-full font-bold py-2.5 md:py-3 px-5 md:px-6 rounded-lg shadow-md transition-all duration-200 text-sm ${
                isSoldOut
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-[#659A9B] to-[#335E77] hover:from-[#335E77] hover:to-[#659A9B] text-white hover:shadow-lg'
              }`}
            >
              {isSoldOut ? 'Sold Out' : 'Book Now'}
            </button>
          </div>
        </div>

        {/* Compact Info Row: Baggage + Refund + Details */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 md:gap-0 mt-3 pt-3 border-t border-gray-100">
          {/* Left: Baggage & Refund */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* Baggage */}
            <div className="flex items-center gap-1.5">
              <Luggage className="w-3.5 h-3.5 text-[#659A9B]" />
              <span className="text-[10px] font-semibold text-gray-700">{cabinBaggage} + {baggage}</span>
            </div>
            
            {/* Separator */}
            <div className="w-px h-4 bg-gray-300"></div>

            {/* Refundable Status */}
            <div className={`flex items-center gap-1.5 px-2 py-1 rounded ${
              isRefundable 
                ? 'bg-green-50 text-green-700' 
                : 'bg-red-50 text-red-700'
            }`}>
              {isRefundable ? (
                <RefreshCcw className="w-3 h-3" />
              ) : (
                <Ban className="w-3 h-3" />
              )}
              <span className="text-[10px] font-bold">{refundableText}</span>
            </div>
          </div>

          {/* Right: Flight Details Button */}
          <button
            onClick={onToggleExpand}
            className="w-full md:w-auto flex items-center justify-center md:justify-start gap-1.5 text-[#659A9B] hover:text-[#335E77] font-bold text-xs transition-colors"
          >
            <span>Flight Details</span>
            {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* Expanded Details - Same as SearchResults */}
      {isExpanded && (
        <div className="border-t border-gray-200 bg-gray-50">
          {/* Tabs */}
          <div className="flex border-b border-gray-200 bg-white px-4">
            <button
              onClick={() => setActiveTab({ ...activeTab, [flight.id]: 'journey' })}
              className={`px-5 py-2.5 text-xs font-semibold border-b-2 transition-colors ${
                (activeTab[flight.id] || 'journey') === 'journey'
                  ? 'border-[#659A9B] text-[#659A9B]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Journey Details
            </button>
            <button
              onClick={() => {
                setActiveTab({ ...activeTab, [flight.id]: 'fareRules' });
                onFareRulesOpen && onFareRulesOpen();
              }}
              className={`px-5 py-2.5 text-xs font-semibold border-b-2 transition-colors ${
                activeTab[flight.id] === 'fareRules'
                  ? 'border-[#659A9B] text-[#659A9B]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Fare Rules
            </button>
          </div>

          {/* Tab Content */}
          <div className="p-4">
            {/* Journey Tab - Using DesktopFlightDetails component */}
            {(activeTab[flight.id] || 'journey') === 'journey' && (
              <div>
                <DesktopFlightDetails 
                  flight={flight}
                  displayFlight={{ ...flight, ...(selectedFare || {}) }}
                  onwardSegments={flight.onwardSegments}
                  returnSegments={flight.returnSegments}
                  isCombo={true}
                  airlineLogos={airlineLogos}
                />
              </div>
            )}

            {/* Fare Rules Tab */}
            {activeTab[flight.id] === 'fareRules' && (
              <div>
                {loadingFareRulesData ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#659A9B]"></div>
                    <span className="ml-3 text-sm text-gray-500">Loading fare rules…</span>
                  </div>
                ) : fareRulesData?.error ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                    <p className="text-sm text-red-600">{fareRulesData.error}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <h4 className="text-xs font-bold text-[#1B375B] uppercase tracking-wider">Fare Rules &amp; Conditions</h4>
                      {(selectedFare?.fareType || flight.fareType) && (
                        <span className="text-[10px] font-semibold text-[#659A9B] bg-teal-50 px-2 py-0.5 rounded-full border border-teal-200">
                          {selectedFare?.fareType || flight.fareType}
                        </span>
                      )}
                    </div>
                    <FareRulesCompact
                      normalized={fareRulesData?._normalized}
                      compact="default"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Multiple Fare Options - Responsive Layout */}
      {flight.fareOptions && flight.fareOptions.length > 1 && (
        <div className="border-t border-gray-200/80 bg-gray-50/30 px-3 md:px-5 py-3 rounded-b-xl">
          <p className="text-[10px] font-bold text-[#1B375B] uppercase tracking-wider mb-2">
            {flight.fareOptions.length} fare options - Select one:
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {flight.fareOptions.map((fare, idx) => {
              const isSelected = selectedFare?.priceId === fare.priceId;
              const fareInfo = getFareTypeInfo(fare.fareType);
              
              return (
                <button
                  key={idx}
                  type="button"
                  disabled={isSoldOut}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!isSoldOut) onFareSelect(fare);
                  }}
                  className={`p-2.5 border-2 rounded-lg transition-all text-left relative ${
                    isSoldOut ? 'opacity-50 cursor-not-allowed' : ''
                  } ${
                    isSelected
                      ? 'border-teal-500 bg-teal-50 shadow-md'
                      : 'bg-white border-gray-200 hover:border-teal-300 hover:shadow-sm'
                  }`}
                >
                  {/* Checkmark Badge - Top Right */}
                  {isSelected && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-teal-500 rounded-full flex items-center justify-center">
                      <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                  
                  {/* Fare Type Badge */}
                  <span className={`inline-block text-[9px] font-bold px-1.5 py-0.5 rounded ${fareInfo.bgColor} ${fareInfo.textColor} mb-1.5`}>
                    {fareInfo.label}
                  </span>
                  
                  {/* Price */}
                  <p className="text-base font-extrabold text-[#1B375B] leading-tight">₹{fare.price.toLocaleString()}</p>
                  
                  {/* Baggage - Compact */}
                  <p className="text-[9px] text-gray-500 mt-0.5 truncate">{fare.baggage}</p>
                  
                  {/* Refundable Badge */}
                  {fare.refundable && (
                    <div className="flex items-center gap-0.5 mt-1">
                      <svg className="w-2.5 h-2.5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-[9px] text-green-600 font-semibold">Refund</span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ComboFlightCard;
